/**
 * OccupationalRisk Details Page
 * Hero header + StatCards + SectionCards layout (same as ProviderDetailsPage)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Chip,
  Alert,
  alpha,
  useTheme,
} from '@mui/material';
import {
  Edit,
  ArrowBack,
  Shield,
  Warning,
  Gavel,
  Science,
  HealthAndSafety,
  Description,
  Category,
  Assessment,
  OpenInNew,
  TrackChanges,
} from '@mui/icons-material';
import {
  PageShell,
  DataTable,
  IdentityBanner,
  InfoItem,
  SectionCard,
  StatCard,
  type DataTableColumn,
  RouteActionButton,
} from '../../components';
import { pode } from '../../authorization/pode';
import { useVeredito } from '../../authorization/useVeredito';
import {
  buscarOndeORiscoEstaAplicado,
  type AplicacaoDoRisco,
  type OndeORiscoEstaAplicado,
} from '../../services/ondeORiscoEstaAplicadoService';
import { montarEndereco } from '../../components/applications/enderecoDoAssistente';
import { ROTULO_DO_NIVEL } from '../../types/nivelDeAplicacao';
import { EXPOSURE_LEVEL_LABELS, ExposureLevelColors } from '../../types/riskApplication';
import { OCC_REPORT_TYPE_LABELS } from '../../types/occupationalRisk';
import {
  RISK_MATRIX_TYPE_LABELS,
  RISK_LEVEL_LABELS,
  RISK_LEVEL_COLORS,
  calculateRiskLevel,
  getProbabilityLabels,
  getSeverityLabels,
} from '../../types/riskMatrix';
import type { RiskMatrixType } from '../../types/riskMatrix';
import { occupationalRiskService } from '../../services/occupationalRiskService';
import riskProtectionService, { RiskProtection } from '../../services/riskProtectionService';
import { esocialReferenceService } from '../../services/esocialReferenceService';
import {
  OccupationalRisk,
  RiskType,
  OccupationalRiskStatus,
  ExposureType,
} from '../../types/occupationalRisk';

/* ─── labels ───
 * NOTE: EXAM_TYPE_LABELS / REPORT_TYPE_LABELS / EXPOSURE_*_LABELS are
 * intentionally local to this page and use COMPACT forms (e.g. "ASO",
 * "S-2210") to fit comma-separated listings on the details card. The
 * canonical long labels live in `@/types/occupationalRisk` and are used
 * by the form wizard, where there is room for the full descriptions.
 */

const RISK_TYPE_LABELS: Record<string, string> = {
  [RiskType.PHYSICAL]: 'Físico',
  [RiskType.CHEMICAL]: 'Químico',
  [RiskType.BIOLOGICAL]: 'Biológico',
  [RiskType.ERGONOMIC]: 'Ergonômico',
  [RiskType.ACCIDENT]: 'Acidente',
  [RiskType.OTHER]: 'Outro',
};

const EXPOSURE_TYPE_LABELS: Record<ExposureType, string> = {
  continua: 'Contínua',
  intermitente: 'Intermitente',
  eventual: 'Eventual',
  habitual_permanente: 'Habitual e Permanente',
};

const RISK_TYPE_COLORS: Record<string, string> = {
  [RiskType.PHYSICAL]: '#2e7d32',
  [RiskType.CHEMICAL]: '#c62828',
  [RiskType.BIOLOGICAL]: '#e65100',
  [RiskType.ERGONOMIC]: '#1565c0',
  [RiskType.ACCIDENT]: '#ad1457',
  [RiskType.OTHER]: '#616161',
};

const formatDate = (date?: string | Date): string => {
  if (!date) return '—';
  const d = new Date(date);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

/* ─── Colunas da tabela de proteções ─── */
const PROTECTION_COLUMNS: DataTableColumn<RiskProtection>[] = [
  {
    id: 'type',
    label: 'Tipo',
    render: (p) => (
      <Chip label={p.epiId ? 'EPI' : 'EPC'} size="small" color={p.epiId ? 'primary' : 'secondary'} variant="outlined" />
    ),
  },
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (p) => (
      <>
        <Typography variant="body2" fontWeight={600}>
          {p.epi?.nome ?? p.epc?.nome ?? '—'}
        </Typography>
        {p.epi?.metodo && (
          <Typography variant="caption" color="text.secondary">CA: {p.epi.metodo}</Typography>
        )}
        {p.epc?.revisao && (
          <Typography variant="caption" color="text.secondary">Rev: {p.epc.revisao}</Typography>
        )}
        {p.notes && (
          <Typography variant="caption" display="block" color="text.secondary">{p.notes}</Typography>
        )}
      </>
    ),
  },
  {
    id: 'effective',
    label: 'Eficaz',
    align: 'center',
    render: (p) => (
      <Chip label={p.isEffective ? 'Sim' : 'Não'} size="small" color={p.isEffective ? 'success' : 'error'} />
    ),
  },
];

/* ─── Onde o risco está aplicado (#606, §4.5) ─── */

/** A aplicação de riscos aberta no destino da linha, na aba dos aplicados, com este risco à vista. */
const enderecoDaAplicacao = (aplicacao: AplicacaoDoRisco, riskId: string, voltar: string): string => {
  const { destino } = aplicacao;
  return montarEndereco('/risk-applications', {
    nivel: destino.nivel,
    ids: { [destino.nivel]: destino.id, company: destino.empresaId },
    aba: 'aplicados',
    item: riskId,
    voltar,
  });
};

/** Nome curto do documento: o rótulo do catálogo sem a explicação entre parênteses. */
const nomeCurtoDoDocumento = (rotulo: string): string => rotulo.split(' (')[0];

const contar = (quantidade: number, singular: string, plural: string): string =>
  `${quantidade} ${quantidade === 1 ? singular : plural}`;

const COLUNAS_ONDE_ESTA_APLICADO: DataTableColumn<AplicacaoDoRisco>[] = [
  {
    id: 'destino',
    label: 'Aplicado em',
    primary: true,
    render: (aplicacao) =>
      aplicacao.destino.nivel === 'company' ? (
        <Typography variant="body2" color="text.secondary">Empresa inteira</Typography>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary" component="div">
            {ROTULO_DO_NIVEL[aplicacao.destino.nivel]}
          </Typography>
          <Typography variant="body2" fontWeight={600}>{aplicacao.destino.nome}</Typography>
        </>
      ),
  },
  {
    id: 'empresa',
    label: 'Empresa',
    render: (aplicacao) => (
      <Typography variant="body2" fontWeight={600}>{aplicacao.destino.empresaNome}</Typography>
    ),
  },
  {
    id: 'exposicao',
    label: 'Exposição',
    render: (aplicacao) =>
      aplicacao.exposureLevel ? (
        <Chip
          label={EXPOSURE_LEVEL_LABELS[aplicacao.exposureLevel]}
          color={ExposureLevelColors[aplicacao.exposureLevel]}
          size="small"
          variant="outlined"
        />
      ) : (
        <Typography variant="body2" color="text.secondary">—</Typography>
      ),
  },
  {
    id: 'alcance',
    label: 'Alcança',
    render: (aplicacao) => (
      <Typography variant="body2">
        {contar(aplicacao.alcance.funcionarios, 'funcionário', 'funcionários')}
      </Typography>
    ),
  },
  {
    id: 'documentos',
    label: 'Entra em',
    render: (aplicacao) => {
      // Sem documento marcado o risco sai em todos; marcar é restringir, e é a restrição que se lê.
      if (aplicacao.reportTypes.length === 0) {
        return <Typography variant="body2" color="text.secondary">Sai em todos</Typography>;
      }
      const nomes = aplicacao.reportTypes.map(({ reportType }) =>
        nomeCurtoDoDocumento(OCC_REPORT_TYPE_LABELS[reportType] ?? reportType)
      );
      return <Typography variant="body2">{`Só em: ${nomes.join(', ')}`}</Typography>;
    },
  },
];

/* ══════════════════════════════════════════════════════════════ */

// A descrição do agente vem da Tabela 24 do eSocial.
const TABELA_AGENTES_NOCIVOS = 24;

const OccupationalRiskDetailsPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // "Concluir", na aplicação, volta para cá.
  const aqui = `${location.pathname}${location.search}`;
  const theme = useTheme();
  const { id } = useParams<{ id: string }>();

  const [risk, setRisk] = useState<OccupationalRisk | null>(null);
  const [protections, setProtections] = useState<RiskProtection[]>([]);
  const [loading, setLoading] = useState(true);
  const [esocialAgentDescription, setEsocialAgentDescription] = useState<string | null>(null);
  const [ondeEstaAplicado, setOndeEstaAplicado] = useState<OndeORiscoEstaAplicado | null>(null);
  const [carregandoAplicacoes, setCarregandoAplicacoes] = useState(true);
  const [aplicacoesFalharam, setAplicacoesFalharam] = useState(false);
  // "Abrir" leva à tela de aplicação, que só existe na Plataforma (D13): fora dela, só leitura.
  const abrirAplicacao = useVeredito(
    '/risk-applications',
    pode('risk_application.read', { perspectiva: 'plataforma' })
  );

  const loadData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const [riskData, protData] = await Promise.all([
        occupationalRiskService.getOccupationalRiskById(id),
        riskProtectionService.getProtections(id).catch(() => ({ protections: [] })),
      ]);
      setRisk(riskData);
      setProtections(protData.protections);
    } catch {
      setRisk(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  useEffect(() => {
    if (!id) return;
    setCarregandoAplicacoes(true);
    setAplicacoesFalharam(false);
    buscarOndeORiscoEstaAplicado(id)
      .then(setOndeEstaAplicado)
      .catch(() => {
        setOndeEstaAplicado(null);
        setAplicacoesFalharam(true);
      })
      .finally(() => setCarregandoAplicacoes(false));
  }, [id]);

  // O técnico reconhece o agente nocivo pelo nome, não pelo número da Tabela 24.
  useEffect(() => {
    const codigo = risk?.esocialAgentCode;
    if (!codigo) {
      setEsocialAgentDescription(null);
      return;
    }
    esocialReferenceService
      .lookup(TABELA_AGENTES_NOCIVOS, [codigo])
      .then((itens) => setEsocialAgentDescription(itens[0]?.description ?? null))
      .catch(() => setEsocialAgentDescription(null));
  }, [risk?.esocialAgentCode]);

  if (loading) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/home' },
          { label: 'Riscos Ocupacionais', path: '/occupational-risks' },
          { label: 'Detalhes' },
        ]}
        title="Detalhes do Risco Ocupacional"
      >
        <Box sx={{ textAlign: 'center' }}>
          <CircularProgress />
        </Box>
      </PageShell>
    );
  }

  if (!risk) {
    return (
      <PageShell
        breadcrumbs={[
          { label: 'Início', path: '/home' },
          { label: 'Riscos Ocupacionais', path: '/occupational-risks' },
          { label: 'Detalhes' },
        ]}
        title="Detalhes do Risco Ocupacional"
      >
        <Alert severity="error">Risco ocupacional não encontrado.</Alert>
      </PageShell>
    );
  }

  const riskColor = RISK_TYPE_COLORS[risk.riskType] ?? theme.palette.primary.main;
  const hasRegulatory = risk.unhealthyAgent || risk.hazardousAgent || risk.socialSecurityAgent || risk.legalFoundation || risk.esocialAgentCode || risk.esocialAgentCodeLegacy || risk.inssAgentCode || risk.nr15Annex || risk.nr16Annex || risk.specialRetirementYears;
  const hasTechnical = risk.actionLevel || risk.toleranceLimit || risk.measurementUnit || risk.evaluationMethod || risk.defaultPeriodicity;
  const hasExposure = risk.penetrationRoute || risk.propagationMedium || risk.evaluationMethodology || risk.healthDamages;
  const hasAdditional = risk.determiningFactors || risk.emergencyPreparation || risk.exposureForm || risk.exposureType;
  const hasMatrix = risk.riskMatrixType && risk.riskProbability && risk.riskSeverity;
  const aplicacoes = ondeEstaAplicado?.aplicacoes ?? [];
  const resumoDasAplicacoes = ondeEstaAplicado?.resumo;
  let valorDoAplicadoEm = 'Nenhuma empresa';
  if (carregandoAplicacoes) valorDoAplicadoEm = 'Carregando…';
  else if (aplicacoesFalharam) valorDoAplicadoEm = '—';
  else if (resumoDasAplicacoes && resumoDasAplicacoes.empresas > 0) {
    valorDoAplicadoEm = contar(resumoDasAplicacoes.empresas, 'empresa', 'empresas');
  }

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Início', path: '/home' },
        { label: 'Riscos Ocupacionais', path: '/occupational-risks' },
        { label: risk.riskFactorName },
      ]}
      actions={
        <>
          <Button variant="outlined" size="small" startIcon={<ArrowBack />} onClick={() => navigate('/occupational-risks')}>
            Voltar
          </Button>
          <RouteActionButton
            para="/occupational-risks/:id/edit"
            exige={pode('occupational_risk.update')}
            aoAgir={() => navigate(`/occupational-risks/${id}/edit`)}
            variant="contained"
            size="small"
            startIcon={<Edit />}
          >
            Editar
          </RouteActionButton>
        </>
      }
    >
        {/* ═══ T1: IDENTIDADE ═══ */}
        <IdentityBanner
          avatar={<Warning sx={{ fontSize: 32 }} />}
          name={risk.riskFactorName}
          color={riskColor}
          status={
            <Chip
              label={risk.status === OccupationalRiskStatus.ACTIVE ? 'Ativo' : 'Inativo'}
              color={risk.status === OccupationalRiskStatus.ACTIVE ? 'success' : 'default'}
              size="small"
              sx={{ fontWeight: 600 }}
            />
          }
          context={
            <>
              <strong>Perigo ou Fator de Risco:</strong> {risk.hazard}
              {risk.risk ? ` · ${risk.risk}` : ''}
            </>
          }
          identifiers={
            <>
              <Chip
                label={RISK_TYPE_LABELS[risk.riskType] ?? risk.riskType}
                size="small"
                sx={{ fontWeight: 600, bgcolor: alpha(riskColor, 0.1), color: riskColor }}
              />
              {risk.unhealthyAgent && <Chip label="NR-15" size="small" color="warning" />}
              {risk.hazardousAgent && <Chip label="NR-16" size="small" color="error" />}
              {risk.socialSecurityAgent && <Chip label="Previd." size="small" color="info" />}
            </>
          }
        />

        {/* ═══ T2: STAT CARDS ═══ */}
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<Category fontSize="small" />}
              label="Tipo de Risco"
              value={RISK_TYPE_LABELS[risk.riskType] ?? risk.riskType}
              color={riskColor}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<Warning fontSize="small" />}
              label="Classificação NR"
              value={
                [risk.unhealthyAgent && 'NR-15', risk.hazardousAgent && 'NR-16']
                  .filter(Boolean)
                  .join(' / ') || 'Nenhuma'
              }
              color={risk.unhealthyAgent || risk.hazardousAgent ? '#e65100' : '#9e9e9e'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<Shield fontSize="small" />}
              label="Proteções"
              value={`${protections.length} ${protections.length === 1 ? 'vínculo' : 'vínculos'}`}
              sublabel={protections.filter(p => p.isEffective).length > 0 ? `${protections.filter(p => p.isEffective).length} eficaz(es)` : undefined}
              color={theme.palette.primary.main}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<HealthAndSafety fontSize="small" />}
              label="Status"
              value={risk.status === OccupationalRiskStatus.ACTIVE ? 'Ativo' : 'Inativo'}
              sublabel={`Criado em ${formatDate(risk.createdAt)}`}
              color={risk.status === OccupationalRiskStatus.ACTIVE ? theme.palette.success.main : '#9e9e9e'}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 'grow' }}>
            <StatCard
              icon={<TrackChanges fontSize="small" />}
              label="Aplicado em"
              value={valorDoAplicadoEm}
              sublabel={
                resumoDasAplicacoes && resumoDasAplicacoes.empresas > 0
                  ? contar(resumoDasAplicacoes.funcionariosExpostos, 'funcionário exposto', 'funcionários expostos')
                  : undefined
              }
              color={theme.palette.info.dark}
            />
          </Grid>
        </Grid>

        {/* ═══ ONDE ESTÁ APLICADO (#606) ═══ */}
        <Box sx={{ mb: 3 }}>
          <SectionCard
            title="Onde está aplicado"
            icon={<TrackChanges fontSize="small" />}
            accentColor={theme.palette.info.dark}
            badge={carregandoAplicacoes || aplicacoesFalharam ? undefined : String(aplicacoes.length)}
          >
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Cada linha é uma aplicação deste risco.
              {abrirAplicacao.liberado ? ' "Abrir" leva ao destino, onde dá para mudar ou remover.' : ''}
            </Typography>
            {aplicacoesFalharam ? (
              <Alert severity="error">
                Não foi possível carregar onde este risco está aplicado. Recarregue a página para tentar de novo.
              </Alert>
            ) : (
              <DataTable
                columns={COLUNAS_ONDE_ESTA_APLICADO}
                rows={aplicacoes}
                getRowId={(aplicacao) => String(aplicacao.id)}
                loading={carregandoAplicacoes}
                emptyState={{
                  title: 'Este risco ainda não foi aplicado',
                  description:
                    'Um risco só entra nos documentos de uma empresa depois de aplicado a ela, a uma unidade, setor, cargo ou funcionário, na tela de Aplicação de Riscos.',
                }}
                actions={
                  abrirAplicacao.liberado
                    ? [
                        {
                          icon: <OpenInNew fontSize="small" />,
                          tooltip: 'Abrir',
                          onClick: (aplicacao) => navigate(enderecoDaAplicacao(aplicacao, String(risk.id), aqui)),
                        },
                      ]
                    : undefined
                }
              />
            )}
          </SectionCard>
        </Box>

        {/* ═══ T3: DETAIL SECTIONS ═══ */}
        <Grid container spacing={2.5} sx={{ mb: 3 }}>
          {/* Classificação Regulatória */}
          {hasRegulatory && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Classificação Regulatória" icon={<Gavel fontSize="small" />} accentColor="#e65100">
                <Grid container spacing={2}>
                  {risk.unhealthyAgent && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Agente Insalubre" value={risk.unhealthyAgent} /></Grid>
                  )}
                  {risk.hazardousAgent && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Agente Perigoso" value={risk.hazardousAgent} /></Grid>
                  )}
                  {risk.socialSecurityAgent && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Agente Previdenciário" value={risk.socialSecurityAgent} /></Grid>
                  )}
                  {risk.legalFoundation && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Fundamentação Legal" value={risk.legalFoundation} /></Grid>
                  )}
                  {risk.esocialAgentCode && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Código do agente nocivo (Tabela 24)"
                        value={esocialAgentDescription ? `${risk.esocialAgentCode} — ${esocialAgentDescription}` : risk.esocialAgentCode}
                      />
                    </Grid>
                  )}
                  {!risk.esocialAgentCode && risk.esocialAgentCodeLegacy && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Código do agente nocivo (Tabela 24)"
                        value={`Código anterior, não corresponde à Tabela 24: ${risk.esocialAgentCodeLegacy}`}
                      />
                    </Grid>
                  )}
                  {risk.inssAgentCode && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Código do Agente no INSS (Anexo IV)" value={risk.inssAgentCode} />
                    </Grid>
                  )}
                  {risk.nr15Annex && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Anexo da NR-15" value={`Anexo ${risk.nr15Annex}`} />
                    </Grid>
                  )}
                  {risk.nr16Annex && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem label="Anexo da NR-16" value={risk.nr16Annex} />
                    </Grid>
                  )}
                  {risk.specialRetirementYears && (
                    <Grid size={{ xs: 12, sm: 6 }}>
                      <InfoItem
                        label="Aposentadoria Especial"
                        value={`${risk.specialRetirementYears} anos de exposição`}
                      />
                    </Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Matriz de Risco */}
          {hasMatrix && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Matriz de Risco" icon={<Assessment fontSize="small" />} accentColor={
                (() => {
                  const level = calculateRiskLevel(risk.riskMatrixType as RiskMatrixType, risk.riskProbability!, risk.riskSeverity!);
                  return level ? RISK_LEVEL_COLORS[level] : '#616161';
                })()
              }>
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <InfoItem label="Tipo de Matriz" value={RISK_MATRIX_TYPE_LABELS[risk.riskMatrixType as RiskMatrixType]} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Probabilidade"
                      value={`${risk.riskProbability} — ${getProbabilityLabels(risk.riskMatrixType as RiskMatrixType)[risk.riskProbability!]}`}
                    />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <InfoItem
                      label="Severidade"
                      value={`${risk.riskSeverity} — ${getSeverityLabels(risk.riskMatrixType as RiskMatrixType)[risk.riskSeverity!]}`}
                    />
                  </Grid>
                  <Grid size={12}>
                    {(() => {
                      const level = calculateRiskLevel(risk.riskMatrixType as RiskMatrixType, risk.riskProbability!, risk.riskSeverity!);
                      if (!level) return null;
                      return (
                        <Box>
                          <Typography variant="caption" color="text.secondary" fontWeight={500}>
                            Nível de Risco
                          </Typography>
                          <Box sx={{ mt: 0.5 }}>
                            <Chip
                              label={RISK_LEVEL_LABELS[level]}
                              sx={{ fontWeight: 700, bgcolor: RISK_LEVEL_COLORS[level], color: '#fff' }}
                            />
                          </Box>
                        </Box>
                      );
                    })()}
                  </Grid>
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Parâmetros Técnicos */}
          {hasTechnical && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Parâmetros Técnicos" icon={<Science fontSize="small" />} accentColor="#1565c0">
                <Grid container spacing={2}>
                  {risk.actionLevel && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Nível de Ação" value={risk.actionLevel} /></Grid>
                  )}
                  {risk.toleranceLimit && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Limite de Tolerância" value={risk.toleranceLimit} /></Grid>
                  )}
                  {risk.measurementUnit && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Unidade de Medida" value={risk.measurementUnit} /></Grid>
                  )}
                  {risk.evaluationMethod && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Método de Avaliação" value={risk.evaluationMethod} /></Grid>
                  )}
                  {risk.defaultPeriodicity && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Periodicidade (meses)" value={risk.defaultPeriodicity} /></Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Exposição e Danos */}
          {hasExposure && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Exposição e Danos à Saúde" icon={<HealthAndSafety fontSize="small" />} accentColor="#ad1457">
                <Grid container spacing={2}>
                  {risk.penetrationRoute && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Via de Penetração" value={risk.penetrationRoute} /></Grid>
                  )}
                  {risk.propagationMedium && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Meio de Propagação" value={risk.propagationMedium} /></Grid>
                  )}
                  {risk.evaluationMethodology && (
                    <Grid size={12}><InfoItem label="Metodologia de Avaliação" value={risk.evaluationMethodology} fullWidth /></Grid>
                  )}
                  {risk.healthDamages && (
                    <Grid size={12}><InfoItem label="Danos à Saúde" value={risk.healthDamages} fullWidth /></Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Proteções (EPC/EPI) */}
          <Grid size={{ xs: 12, md: 6 }}>
            <SectionCard
              title={`Proteções (${protections.length})`}
              icon={<Shield fontSize="small" />}
              accentColor={theme.palette.primary.main}
            >
              {protections.length === 0 ? (
                <Typography variant="body2" color="text.secondary">
                  Nenhuma proteção vinculada a este risco.
                </Typography>
              ) : (
                <DataTable
                  columns={PROTECTION_COLUMNS}
                  rows={protections}
                  getRowId={(p) => p.id}
                  emptyState={{
                    title: 'Nenhuma proteção vinculada a este risco',
                    description:
                      'As proteções são os EPIs e EPCs vinculados ao risco na tela de edição.',
                  }}
                />
              )}
            </SectionCard>
          </Grid>

          {/* Informações Adicionais */}
          {hasAdditional && (
            <Grid size={{ xs: 12, md: 6 }}>
              <SectionCard title="Informações Adicionais" icon={<Description fontSize="small" />} accentColor="#2e7d32">
                <Grid container spacing={2}>
                  {risk.determiningFactors && (
                    <Grid size={12}><InfoItem label="Fatores Determinantes" value={risk.determiningFactors} fullWidth /></Grid>
                  )}
                  {risk.emergencyPreparation && (
                    <Grid size={12}><InfoItem label="Preparação para Emergências" value={risk.emergencyPreparation} fullWidth /></Grid>
                  )}
                  {risk.exposureForm && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Forma de Exposição" value={risk.exposureForm} /></Grid>
                  )}
                  {risk.exposureType && (
                    <Grid size={{ xs: 12, sm: 6 }}><InfoItem label="Tipo de Exposição" value={EXPOSURE_TYPE_LABELS[risk.exposureType] ?? risk.exposureType} /></Grid>
                  )}
                </Grid>
              </SectionCard>
            </Grid>
          )}

          {/* Descrição Completa */}
          <Grid size={12}>
            <SectionCard title="Descrição do Risco" icon={<Description fontSize="small" />} accentColor="#616161">
              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                {risk.risk}
              </Typography>
            </SectionCard>
          </Grid>
        </Grid>

        {/* ── Metadata ── */}
        <Typography variant="caption" color="text.secondary">
          Criado em {formatDate(risk.createdAt)} · Atualizado em {formatDate(risk.updatedAt)}
        </Typography>
    </PageShell>
  );
};

export default OccupationalRiskDetailsPage;
